import {createStylesParams, StyleParamType, wixColorParam, wixFontParam} from '@wix/tpa-settings';
import {CheckoutButtonsPosition} from '@wix/wixstores-client-core';
import {StyleType, wixColor, wixFont, CssColor, CssFont} from '@wix/editor-platform-ooi-metadata-resolver';
import {StyleParam} from '../../common/constants';
import type {CustomCssVarsFn} from '@wix/yoshi-flow-editor';
import {addUnitsIfNeeded, toDisplayProperty} from '../../domain/utils/styleParams.utils';

type IStylesParams = {
  [StyleParam.CartBackground]: StyleParamType.Color;
  [StyleParam.CartDividersColor]: StyleParamType.Color;
  [StyleParam.CartTextColor]: StyleParamType.Color;
  [StyleParam.CartTextFontStyle]: StyleParamType.Font;
  [StyleParam.CartTitlesColor]: StyleParamType.Color;
  [StyleParam.CartTitlesFontStyle]: StyleParamType.Font;
  [StyleParam.CartLinksColor]: StyleParamType.Color;
  [StyleParam.CartDiscountNameColor]: StyleParamType.Color;
  [StyleParam.CartButtonBorderColor]: StyleParamType.Color;
  [StyleParam.CartButtonBackgroundColor]: StyleParamType.Color;
  [StyleParam.CartButtonTextColor]: StyleParamType.Color;
  [StyleParam.CartButtonTextFont]: StyleParamType.Font;
  [StyleParam.CheckoutButtonsPosition]: StyleParamType.Number;
  [StyleParam.ShowShipping]: StyleParamType.Boolean;
  [StyleParam.ShowTax]: StyleParamType.Boolean;
  [StyleParam.ShowContinueShopping]: StyleParamType.Boolean;
  [StyleParam.ShowCoupon]: StyleParamType.Boolean;
  [StyleParam.ShowBuyerNote]: StyleParamType.Boolean;
  [StyleParam.ShowExpressCheckout]: StyleParamType.Boolean;
  [StyleParam.sidePaddingSliderOnDesktop]: StyleParamType.Number;
  [StyleParam.CornerRadius]: StyleParamType.Number;
  [StyleParam.CartButtonBorderSize]: StyleParamType.Number;
};

export default createStylesParams<IStylesParams>({
  [StyleParam.CartBackground]: {
    type: StyleParamType.Color,
    key: StyleParam.CartBackground,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'var(--wst-primary-background-color)',
        opacity: 0,
      },
    },
  },
  [StyleParam.CartTitlesColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartTitlesColor,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstHeading2Color),
    },
  },
  [StyleParam.CartTitlesFontStyle]: {
    type: StyleParamType.Font,
    key: StyleParam.CartTitlesFontStyle,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading2Font),
    },
  },
  [StyleParam.CartTextColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartTextColor,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'Base color used for cart item names, prices, quantities, subtotal, and input fields.',
      },
    },
  },
  [StyleParam.CartTextFontStyle]: {
    type: StyleParamType.Font,
    key: StyleParam.CartTextFontStyle,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'Base font used for cart item names, prices, quantities, subtotal, and input fields.',
      },
    },
  },
  [StyleParam.CartDividersColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartDividersColor,
    getDefaultValue: wixColorParam('color-5', 0.2),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'var(--wst-system-line-1-color)',
        opacity: 0.2,
      },
    },
  },
  [StyleParam.CartLinksColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartLinksColor,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: {
        description: 'Color of the text buttons for adding promo code and buyer note.',
      },
    },
  },
  [StyleParam.CartDiscountNameColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartDiscountNameColor,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: {
        description: 'Color of discount names displayed on cart items.',
      },
    },
  },
  [StyleParam.CartButtonBorderColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartButtonBorderColor,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
      ai: {
        description: 'Border color of the button that navigates to the checkout page.',
      },
    },
  },
  [StyleParam.CartButtonBackgroundColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartButtonBackgroundColor,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: {
        description: 'Background color of the button that navigates to the checkout page.',
      },
    },
  },
  [StyleParam.CartButtonTextColor]: {
    type: StyleParamType.Color,
    key: StyleParam.CartButtonTextColor,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: {
        description: 'Text color of the button that navigates to the checkout page.',
      },
    },
  },
  [StyleParam.CartButtonTextFont]: {
    type: StyleParamType.Font,
    key: StyleParam.CartButtonTextFont,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph1Font),
      ai: {
        description: 'Font of the button that navigates to the checkout page.',
      },
    },
  },
  [StyleParam.CheckoutButtonsPosition]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(CheckoutButtonsPosition.ABOVE_AND_BELOW_CART),
  },
  [StyleParam.ShowShipping]: {
    type: StyleParamType.Boolean,
    key: StyleParam.ShowShipping,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowTax]: {
    type: StyleParamType.Boolean,
    key: StyleParam.ShowTax,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  [StyleParam.ShowContinueShopping]: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },
  [StyleParam.ShowCoupon]: {
    type: StyleParamType.Boolean,
    key: StyleParam.ShowCoupon,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowBuyerNote]: {
    type: StyleParamType.Boolean,
    key: StyleParam.ShowBuyerNote,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowExpressCheckout]: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  [StyleParam.sidePaddingSliderOnDesktop]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  [StyleParam.CornerRadius]: {
    type: StyleParamType.Number,
    key: StyleParam.CornerRadius,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description:
          'Border radius of the button that navigates to the checkout page. values in px are between 0 and 20.',
      },
    },
  },
  [StyleParam.CartButtonBorderSize]: {
    type: StyleParamType.Number,
    key: StyleParam.CartButtonBorderSize,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description: 'Border width of the button that navigates to the checkout page.',
      },
    },
  },
});

export const customCssVars: CustomCssVarsFn = ({styleParams: {booleans, numbers}}) => {
  return {
    expressCheckoutButtonDisplay: toDisplayProperty(booleans?.[StyleParam.ShowExpressCheckout]),
    ...(numbers?.[StyleParam.CornerRadius] && {
      [StyleParam.CornerRadius]: addUnitsIfNeeded(numbers?.[StyleParam.CornerRadius]),
    }),
    shouldShowTopCheckoutButtonInMobile: toDisplayProperty(
      numbers?.[StyleParam.CheckoutButtonsPosition] === Number(CheckoutButtonsPosition.ABOVE_AND_BELOW_CART)
    ),
  };
};
